import { useState } from 'react';
import styled from 'styled-components';

import { INSTRUCTIONS_LIMIT, MappingTarget } from 'services/api/admin/mappings';
import { colors } from 'styles/theme/colors';
import ChevronDown from 'svg/ChevronDown';

import { Chip, Muted } from './Chips';
import OperationProblem from './OperationProblem';
import { PendingOperation } from './operations';

type InstructionsProps = {
  title: string;
  stored: string | null;
  pending: string | null;
  problem: PendingOperation | undefined;
  disabled: boolean;
  onChange: (text: string | null) => void;
  onUndo: (slot: string) => void;
};

export function AgentInstructions({ title, stored, pending, problem, disabled, onChange, onUndo }: InstructionsProps) {
  const [open, setOpen] = useState(pending !== null);
  const text = pending ?? stored ?? '';
  const over = text.length > INSTRUCTIONS_LIMIT;

  return (
    <Section>
      <SectionHeader onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>
          Agent instructions for automatic {title.toLowerCase()}{' '}
          {pending !== null && <Chip $tone='amber'>Pending</Chip>}
        </span>
        <Chevron $open={open} color={colors.black50} />
      </SectionHeader>
      {open && (
        <SectionBody>
          <Textarea
            value={text}
            disabled={disabled}
            rows={4}
            placeholder="Tell the agent anything specific to this client, e.g. 'SL means Sugar Land.'"
            onChange={(e) => onChange(e.target.value === (stored ?? '') ? null : e.target.value)}
          />
          <Counter $over={over}>
            {text.length.toLocaleString()} / {INSTRUCTIONS_LIMIT.toLocaleString()} · saves with the configuration
          </Counter>
        </SectionBody>
      )}
      <OperationProblem op={problem} onUndo={onUndo} />
    </Section>
  );
}

type FallbackProps = {
  stored: MappingTarget | null;
  pending: string[] | null | undefined;
  problem: PendingOperation | undefined;
  disabled: boolean;
  onChange: (target: string[] | null | undefined) => void;
  onKeepMine: (op: PendingOperation) => void;
  onUndo: (slot: string) => void;
};

function lines(target: MappingTarget | null | undefined) {
  if (!target) return [];
  return Array.isArray(target) ? target : [target];
}

export function FallbackRule({ stored, pending, problem, disabled, onChange, onKeepMine, onUndo }: FallbackProps) {
  const storedLines = lines(stored);
  const [draft, setDraft] = useState((pending === undefined ? storedLines : lines(pending)).join('\n'));

  const update = (value: string) => {
    setDraft(value);
    const next = value
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    const same = next.length === storedLines.length && next.every((l, i) => l === storedLines[i]);
    onChange(same ? undefined : next.length ? next : null);
  };

  return (
    <Section>
      <SectionTitle>Wildcard mappings {pending !== undefined && <Chip $tone='amber'>Pending</Chip>}</SectionTitle>
      <Muted>Tried for any value with no mapping, in order. One pattern per line; %{'{value}'} is the raw value.</Muted>
      <Textarea value={draft} disabled={disabled} rows={2} onChange={(e) => update(e.target.value)} />
      <OperationProblem op={problem} onKeepMine={onKeepMine} onUndo={onUndo} />
    </Section>
  );
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Chevron = styled(ChevronDown)<{ $open: boolean }>`
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '180deg')});
  transform-origin: center;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.blue100};
  }
`;

const Counter = styled.span<{ $over: boolean }>`
  align-self: flex-end;
  font-size: 12px;
  color: ${({ $over }) => ($over ? colors.red100 : colors.black50)};
`;
